import { serializeTenantQueryArgs } from '@core/api/tenantContext';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api/baseQuery';

export const notificationsApi = createApi({
  serializeQueryArgs: serializeTenantQueryArgs,
  reducerPath: 'notificationsApi',
  baseQuery,
  tagTypes: ['NotificationChannel', 'EventConfig', 'Preference'],
  endpoints: (builder) => ({
    // --- Channels ---
    getChannels: builder.query({
      query: () => '/api/notifications/channels/',
      transformResponse: (response) => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'NotificationChannel', id: uuid })),
              { type: 'NotificationChannel', id: 'LIST' },
            ]
          : [{ type: 'NotificationChannel', id: 'LIST' }],
    }),

    createChannel: builder.mutation({
      query: (data) => ({
        url: '/api/notifications/channels/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'NotificationChannel', id: 'LIST' }],
    }),

    updateChannel: builder.mutation({
      query: ({ uuid, ...data }) => ({
        url: `/api/notifications/channels/${uuid}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: 'NotificationChannel', id: uuid },
        { type: 'NotificationChannel', id: 'LIST' },
      ],
    }),

    deleteChannel: builder.mutation({
      query: (uuid) => ({
        url: `/api/notifications/channels/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, uuid) => [
        { type: 'NotificationChannel', id: uuid },
        { type: 'NotificationChannel', id: 'LIST' },
      ],
    }),

    // --- Event Configs ---
    getEventConfigs: builder.query({
      query: () => '/api/notifications/event-configs/',
      transformResponse: (response) => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'EventConfig', id: uuid })),
              { type: 'EventConfig', id: 'LIST' },
            ]
          : [{ type: 'EventConfig', id: 'LIST' }],
    }),

    createEventConfig: builder.mutation({
      query: (data) => ({
        url: '/api/notifications/event-configs/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'EventConfig', id: 'LIST' }],
    }),

    updateEventConfig: builder.mutation({
      query: ({ uuid, ...data }) => ({
        url: `/api/notifications/event-configs/${uuid}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: 'EventConfig', id: uuid },
        { type: 'EventConfig', id: 'LIST' },
      ],
    }),

    deleteEventConfig: builder.mutation({
      query: (uuid) => ({
        url: `/api/notifications/event-configs/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'EventConfig', id: 'LIST' }],
    }),

    // --- Preferences ---
    getPreferences: builder.query({
      query: () => '/api/notifications/preferences/',
      providesTags: [{ type: 'Preference', id: 'LIST' }],
    }),

    updatePreference: builder.mutation({
      query: ({ event_type, is_muted }) => ({
        url: `/api/notifications/preferences/${event_type}/`,
        method: 'PUT',
        body: { is_muted },
      }),
      invalidatesTags: [{ type: 'Preference', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetChannelsQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useGetEventConfigsQuery,
  useCreateEventConfigMutation,
  useUpdateEventConfigMutation,
  useDeleteEventConfigMutation,
  useGetPreferencesQuery,
  useUpdatePreferenceMutation,
} = notificationsApi;
